import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { inChildLayer, isTopLayer, popLayer, pushLayer } from "./layers";

type Placement = "bottom-start" | "bottom-end" | "top-start" | "right-start";

interface PanelProps {
  anchor: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  placement?: Placement;
  className?: string;
  /** match the anchor's width */
  matchWidth?: boolean;
  offset?: number;
}

export function PopoverPanel({ anchor, open, onClose, children, placement = "bottom-start", className, matchWidth, offset = 4 }: PanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [layer, setLayer] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; width?: number } | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const place = useCallback(() => {
    const a = anchor.current;
    const panel = panelRef.current;
    if (!a || !panel) return;
    const r = a.getBoundingClientRect();
    const pw = panel.offsetWidth;
    const ph = panel.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = placement.startsWith("top") ? r.top - ph - offset : r.bottom + offset;
    let left = placement.endsWith("end") ? r.right - pw : r.left;
    if (placement === "right-start") {
      top = r.top;
      left = r.right + offset;
      if (left + pw > vw - 8) left = r.left - pw - offset;
    }
    if (placement.startsWith("bottom") && top + ph > vh - 8 && r.top - ph - offset > 8) top = r.top - ph - offset;
    top = Math.max(8, Math.min(top, vh - ph - 8));
    left = Math.max(8, Math.min(left, vw - pw - 8));
    setPos({ top, left, width: matchWidth ? r.width : undefined });
  }, [anchor, placement, offset, matchWidth]);

  useEffect(() => {
    if (!open) return;
    const id = pushLayer();
    setLayer(id);
    return () => {
      popLayer(id);
      setPos(null);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !layer) return;
    place();
    const ro = new ResizeObserver(place);
    if (panelRef.current) ro.observe(panelRef.current);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, layer, place]);

  useEffect(() => {
    if (!open || !layer) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchor.current?.contains(t) || inChildLayer(e.target, layer)) return;
      onCloseRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isTopLayer(layer)) {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, layer, anchor]);

  if (!open) return null;
  return createPortal(
    <div
      ref={panelRef}
      data-layer={layer}
      className={cn(
        "fixed z-50 max-h-[min(460px,calc(100vh-16px))] overflow-auto rounded-lg bg-elevated shadow-pop scroll-thin",
        className,
      )}
      // opacity (not visibility) keeps autoFocus working before the panel is positioned
      style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, width: pos?.width, opacity: pos ? 1 : 0, pointerEvents: pos ? undefined : "none" }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}

interface PopoverProps extends Omit<PanelProps, "anchor" | "open" | "onClose" | "children"> {
  trigger: (props: { ref: RefObject<HTMLButtonElement | null>; onClick: (e: React.MouseEvent) => void; open: boolean }) => ReactNode;
  children: (close: () => void) => ReactNode;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function Popover({ trigger, children, disabled, onOpenChange, ...panel }: PopoverProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const set = (v: boolean) => {
    setOpen(v);
    onOpenChange?.(v);
  };
  return (
    <>
      {trigger({
        ref,
        open,
        onClick: (e) => {
          e.stopPropagation();
          if (!disabled) set(!open);
        },
      })}
      <PopoverPanel anchor={ref} open={open} onClose={() => set(false)} {...panel}>
        {children(() => set(false))}
      </PopoverPanel>
    </>
  );
}

export function MenuItem({
  icon: Icon,
  children,
  onClick,
  danger,
  active,
  disabled,
  right,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  children: ReactNode;
  onClick?: () => void;
  danger?: boolean;
  active?: boolean;
  disabled?: boolean;
  right?: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm hover:bg-hover disabled:opacity-40",
        danger ? "text-danger" : "text-fg",
        active && "bg-hover",
      )}
    >
      {Icon && <Icon className="size-4 shrink-0 opacity-70" />}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {right}
    </button>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="px-2 pt-2 pb-1 text-xs font-medium text-muted">{children}</div>;
}

export function MenuDivider() {
  return <div className="my-1 h-px bg-line" />;
}
